import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuth } from './AuthContext';
import { Permission, useHasPermission } from '@/utils/roles';
import { NoAccessPage } from '@/components';
import { AUTH_PATHS } from '@/feature/authentication/constants';
import { useMfaSetupGuard } from '@/feature/authentication/hooks/useMfaSetupGuard';
import {
  clearPostLoginRedirect,
  readPostLoginRedirect,
  savePostLoginRedirect,
} from '@/feature/authentication/utils/postLoginRedirect';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermissions?: Permission[];
}

export const ProtectedRoute = ({
  children,
  requiredPermissions = [],
}: ProtectedRouteProps) => {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const location = useLocation();
  const {
    data: mfaGuard,
    isLoading: mfaGuardLoading,
    isFetched: mfaGuardFetched,
  } = useMfaSetupGuard(user?.email);
  const shouldCheckPermissions =
    !authLoading && isAuthenticated && requiredPermissions.length > 0;
  const {
    hasPermission,
    isLoading: userLoading,
    currentUser,
  } = useHasPermission(requiredPermissions ?? [], true, {
    enabled: shouldCheckPermissions,
  });

  const setupRequired = mfaGuard?.setupRequired === true;
  const isCheckingMfaSetup =
    isAuthenticated && !authLoading && mfaGuardLoading && !mfaGuardFetched;
  const currentPath = `${location.pathname}${location.search}`;
  const pendingRedirect = readPostLoginRedirect();

  // Clear only after commit once we've landed on the target (not while
  // returning <Navigate />, and not before auth/MFA bootstrap finishes).
  useEffect(() => {
    if (
      authLoading ||
      isCheckingMfaSetup ||
      !isAuthenticated ||
      setupRequired
    ) {
      return;
    }
    if (pendingRedirect === currentPath) {
      clearPostLoginRedirect();
    }
  }, [
    authLoading,
    isCheckingMfaSetup,
    isAuthenticated,
    setupRequired,
    pendingRedirect,
    currentPath,
  ]);

  if (authLoading || userLoading || isCheckingMfaSetup) {
    return (
      <div
        className="flex h-screen items-center justify-center text-lg"
        aria-label="Loading"
        tabIndex={0}
      >
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    savePostLoginRedirect(currentPath);
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (setupRequired && location.pathname !== AUTH_PATHS.MFA_SETUP_PHONE) {
    return <Navigate to={AUTH_PATHS.MFA_SETUP_PHONE} replace />;
  }

  // Apply pending deep link after auth (covers password race + SSO landing on `/`).
  // Skip while MFA setup is still required to avoid looping between the
  // deep link and the MFA setup route.
  if (
    !setupRequired &&
    pendingRedirect !== AUTH_PATHS.HOME &&
    pendingRedirect !== currentPath
  ) {
    return <Navigate to={pendingRedirect} replace />;
  }

  if (requiredPermissions.length > 0 && currentUser && !hasPermission) {
    return <NoAccessPage />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
